import { httpRequest } from '~/adapters/httpRequest';
import { graphqlRequest, hasGraphQLErrors, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import {
  FetchAttributeResponsesDocument,
  FindSurveysDocument,
  OverallSurveyResultsDocument,
  TimeframeInput,
} from '~/gql/graphql';
import {
  attributeReportingResultsFrom,
  reportingResultsFrom,
  selectedAttributesFrom,
} from '../transformers/survey_reporting.transformer';

export interface ReportingQuery {
  surveyIds: number[];
  selectedAttributes?: { id: number; values: string[] }[] | null;
  basedOnHierarchy?: boolean;
  selectedTimeframe: TimeframeInput | null;
}

const fetchBaseReport = async (query: ReportingQuery) => {
  try {
    const { surveyIds, selectedAttributes, basedOnHierarchy, selectedTimeframe } = query;

    const res = await httpRequest<any>(`/api/reporting/v1/base-report`, {
      method: 'POST',
      body: { surveyIds, selectedAttributes, basedOnHierarchy, selectedTimeframe },
    });

    if (res?.error) return Promise.reject(res.error);
    if (res?.errors) return Promise.reject(res.errors[0]);

    const results = reportingResultsFrom(res?.report, surveyIds);

    return results;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const findSurveys = async (ids: number[]) => {
  try {
    const data = await graphqlRequest<any>(FindSurveysDocument, { ids });

    if (hasGraphQLNetworkError(data)) return Promise.reject(data.error);
    if (hasGraphQLErrors(data)) return Promise.reject(data.errors[0]);

    return data.data?.surveys;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export interface FetchAttributeResponsesQuery {
  companyIds: number[];
  languageId: number;
  surveyIds: number[];
  isHierarchyReport: boolean;
  attributes?: { id: number; values: string[] }[];
  managerReportIds?: string[] | null;
  requestManagerHierarchyData: boolean;
  selectedTimeframe?: TimeframeInput | null;
  /** False for an Open Survey: it has no response rate, and the API rejects those fields for it. */
  includeResponseRates?: boolean;
}

const fetchAttributeResponses = async (query: FetchAttributeResponsesQuery) => {
  try {
    const data = await graphqlRequest<any>(FetchAttributeResponsesDocument, {
      companyIds: query.companyIds,
      languageId: query.languageId,
      surveyIds: query.surveyIds,
      isHierarchyReport: query.isHierarchyReport,
      attributes: query.attributes,
      managerReportIds: query.managerReportIds,
      requestManagerHierarchyData: query.requestManagerHierarchyData,
      selectedTimeframe: query.selectedTimeframe,
      includeResponseRates: query.includeResponseRates,
    });

    if (hasGraphQLNetworkError(data)) return Promise.reject(data.error);
    if (hasGraphQLErrors(data)) return Promise.reject(data.errors[0]);

    return data.data?.attributesRatingResponses;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export interface OverallSurveyResultsQuery {
  surveyIds: number[];
  languageId: number;
  companyIds: number[];
  attributes?: any[];
  isHierarchyReport?: boolean;
  managerReportIds?: string[];
  selectedTimeframe: {
    startTimeUTC: any;
    endTimeUTC: any;
  } | null;
  selectedParticipantId?: number;
}

const overallSurveyResults = async (query: OverallSurveyResultsQuery) => {
  try {
    const data = await graphqlRequest<any>(OverallSurveyResultsDocument, { ...query });

    if (hasGraphQLNetworkError(data)) return Promise.reject(data.error);
    if (hasGraphQLErrors(data)) return Promise.reject(data.errors[0]);

    return data.data?.overallSurveyResults;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export interface FetchAttributeReportQuery {
  surveyIds: number[];
  selectedAttributes: { id: number; values: string[] }[];
  inputAttributeId: number;
  basedOnHierarchy?: boolean;
  requestManagerHierarchies?: boolean;
  selectedTimeframe: TimeframeInput | null;
}

const fetchAttributeReport = async (query: FetchAttributeReportQuery) => {
  try {
    const res = await httpRequest<any>('/api/reporting/v1/attribute-report', {
      method: 'POST',
      body: {
        surveyIds: query.surveyIds,
        basedOnHierarchy: query.basedOnHierarchy,
        inputAttributeId: query.inputAttributeId,
        requestManagerHierarchies: query.requestManagerHierarchies,
        selectedAttributes: selectedAttributesFrom(query.selectedAttributes),
        selectedTimeframe: query.selectedTimeframe,
      },
    });

    if (res.error) return Promise.reject(res.error);

    const results = attributeReportingResultsFrom(res.attributeReport);

    return results;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reportingQueries = {
  fetchBaseReport,
  findSurveys,
  fetchAttributeResponses,
  overallSurveyResults,
  fetchAttributeReport,
};
